import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Download, ArrowRight } from "lucide-react";
import { getShareByToken, type StudioShare } from "@/lib/studio/shares";
import { generateSummaryPdf, downloadBlob } from "@/lib/studio/pdf";

export const Route = createFileRoute("/share/$token")({
  head: () => ({
    meta: [
      { title: "Shared Color Scheme — Michael's Painting" },
      { name: "description", content: "A paint color scheme created in the Michael's Painting AI Paint Studio." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SharePage,
});

function SharePage() {
  const { token } = Route.useParams();
  const [share, setShare] = useState<StudioShare | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getShareByToken(token)
      .then((s) => { if (!cancelled) { if (!s) setErr("This shared scheme no longer exists."); else setShare(s); } })
      .catch((e) => !cancelled && setErr((e as Error).message));
    return () => { cancelled = true; };
  }, [token]);

  async function handlePdf() {
    if (!share) return;
    setPdfBusy(true);
    try {
      const blob = await generateSummaryPdf({
        projectName: share.name || "Color scheme",
        sourceImageUrl: share.source_image_url,
        renderedImageUrl: share.rendered_image_url,
        palette: share.palette,
        shareUrl: `${window.location.origin}/share/${share.token}`,
      });
      downloadBlob(blob, `${(share.name || "color-scheme").replace(/\s+/g, "-").toLowerCase()}.pdf`);
    } finally { setPdfBusy(false); }
  }

  return (
    <section className="min-h-screen bg-[#030303] text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="font-serif text-lg">Michael's Painting</Link>
          <Link to="/studio" className="text-xs uppercase tracking-widest text-amber-200 hover:text-white">Open Studio →</Link>
        </div>

        {!share && !err && (
          <div className="py-32 text-center"><Loader2 className="size-6 animate-spin text-amber-300 mx-auto" /></div>
        )}
        {err && <p className="text-center text-red-400 py-24">{err}</p>}

        {share && (
          <>
            <p className="text-[10px] uppercase tracking-widest text-amber-200 mb-2">Shared color scheme</p>
            <h1 className="font-serif text-3xl sm:text-4xl mb-6">{share.name || "Untitled project"}</h1>

            <div className="rounded-2xl overflow-hidden border border-white/10 bg-zinc-900 mb-8">
              <img
                src={share.rendered_image_url || share.source_image_url}
                alt={share.name || "Shared preview"}
                className="w-full h-auto"
              />
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-8">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Selected colors</p>
              {share.palette.length === 0 ? (
                <p className="text-sm text-zinc-500">No colors saved yet.</p>
              ) : (
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {share.palette.map((p, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-white/[0.02]">
                      <span className="size-10 rounded-lg border border-white/15 flex-none" style={{ backgroundColor: p.hex }} />
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{p.name} · <span className="text-zinc-400">{p.number}</span></p>
                        <p className="text-[11px] text-zinc-500 truncate">{p.brand} · {p.surface} · {p.hex.toUpperCase()}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                onClick={handlePdf}
                disabled={pdfBusy}
                className="inline-flex items-center justify-center gap-2 bg-white text-zinc-950 font-semibold px-5 py-3 rounded-full text-sm hover:bg-amber-200 transition disabled:opacity-50"
              >
                {pdfBusy ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
                Download PDF summary
              </button>
              <Link
                to="/estimate"
                className="inline-flex items-center justify-center gap-2 bg-amber-300 text-zinc-950 font-semibold px-5 py-3 rounded-full text-sm hover:bg-amber-200 transition"
              >
                Request a free estimate <ArrowRight className="size-4" />
              </Link>
            </div>

            <p className="text-[11px] text-zinc-500 mt-8 leading-relaxed">
              Real paint color may vary based on lighting, surface, and finish. For an accurate match, request a free in-person consultation.
            </p>
          </>
        )}
      </div>
    </section>
  );
}